"use client"

import clsx from "clsx"
import React, { forwardRef } from "react"

export type SectionWrapperProps = React.ComponentPropsWithoutRef<"section">

/**
 * SectionWrapper is a layout utility component that standardizes vertical padding and full-width layout
 * for top-level sections across the application.
 *
 * It wraps content in a `<section>` element and applies responsive vertical spacing using utility classes.
 *
 * This is useful for ensuring consistent spacing between content sections in a page or layout.
 *
 * @example
 * ```tsx
 * <SectionWrapper>
 *   <Container>
 *      <Heading>About Us</Heading>
 *      <Text>We are committed to building great things.</Text>
 *   </Container>
 * </SectionWrapper>
 * ```
 */
const SectionWrapper = forwardRef<HTMLDivElement, SectionWrapperProps>(
  (props, ref) => (
    <section
      {...props}
      className={clsx(
        "section-wrapper w-full py-8 md:py-14 lg:py-22",
        props?.className,
      )}
      ref={ref}
    />
  ),
)

SectionWrapper.displayName = "SectionWrapper"

export default SectionWrapper
